/**
 * Тракт микрофона: усиление, выравниватель, шумодав, тембр и затвор — между
 * `getUserMedia` и Opus.
 *
 * Модель нужна тем, кто выбрал сильное подавление; остальные звенья работают
 * всегда. Граф собирается из того, что есть, а отказ любого необязательного
 * звена значит «звук идёт мимо него», а не «звука нет».
 *
 * Ниже — про самое дорогое звено, модель.
 *
 * Штатное подавление у нас уже есть — его молча включает `livekit-client`, —
 * поэтому здесь не «добавить», а «заменить одно другим»: когда модель
 * работает, `noiseSuppression` и `voiceIsolation` выключаются, а
 * эхоподавление и автоусиление остаются. Порядок при этом получается верный
 * сам собой: обрабатывается готовая дорожка, то есть после AEC, а ему нужен
 * неискажённый сигнал.
 *
 * Встраивание идёт через `setProcessor` — точку, которую `livekit-client`
 * держит для таких вещей. Обработчик отдаёт свою дорожку, LiveKit подменяет
 * ею отправляемую, и весь остальной путь — публикация, заглушение, смена
 * устройства — остаётся его.
 *
 * Правило, которому здесь всё подчинено: **микрофон важнее шумодава**. Не
 * собралось, не скачалось, не потянула машина — звук идёт как шёл, а в
 * настройках об этом написано. Немой микрофон из-за упавшего обработчика
 * хуже любого шума.
 */

import { LocalAudioTrack } from "livekit-client";
import type { AudioCaptureOptions, AudioProcessorOptions, Track, TrackProcessor } from "livekit-client";
import { create } from "zustand";
import {
  denoiserBuilt,
  gateSource,
  levelerSource,
  modelBytes,
  modelSettings,
  wasmBytes,
  workletSource,
} from "./load";
import { onPushChange, pushOpen } from "@shared/media/push";

/** Частота, на которой обучена модель. Всё остальное к ней приводится. */
const MODEL_RATE = 48000;

/**
 * Предел подавления в децибелах.
 *
 * Сто и больше libDF читает как «без предела»: шум давится настолько,
 * насколько модель умеет. Ноль означал бы обратное — сквозной пропуск, — так
 * что шкала тут не «сильнее-слабее», а «докуда можно».
 *
 * Без предела и стояло сначала — так задумано у самого libDF. На живом
 * голосе это слышно как обрыв: между словами выходит цифровой ноль, и тишина
 * читается не как «тихо», а как «связь пропала». Тридцать шесть децибел
 * оставляют от фона около 1,6 процента: вдвое меньше прежних тридцати,
 * но всё ещё без полного вырезания спектра.
 *
 * Предел libDF подмешивает обратно исходный спектр (`1 - lim` на очищенный,
 * `lim` на исходный), то есть он же и есть ручка «сколько своего голоса
 * вернуть»: тридцать шесть — 1,6 процента, тридцать — три, двадцать —
 * десять, пятнадцать — восемнадцать.
 *
 * Здесь стояло, что голос за это платит металлическим призвуком и съеденными
 * шипящими. Неправда: так звучало наложение нескольких дорожек разом, и с
 * тех пор это починено. Модель верхом не платит, и слушать её надо заново —
 * возможно, предел можно и поднимать.
 *
 * Чем этот подмес в самом деле платит — так это короткими ударами. Даже
 * полтора процента от сильного щелчка по клавише могут остаться слышны.
 * Разбирается с ним не предел, а затвор (`gate.js`): в паузе он не
 * пропускает ничего вовсе.
 */
const DEFAULT_ATTENUATION = 36;

let attenuation = DEFAULT_ATTENUATION;

/**
 * Узлы модели в проверочных трактах (см. `createPreviewTract`).
 *
 * Свой список, потому что движок у проверки свой и до её узла `ready.node`
 * не дотягивается, — а ручка предела нужнее всего именно там: сравнивать
 * значения ухом удобнее по кнопке «Проверить», чем в живом разговоре.
 */
const previewNodes = new Set<AudioWorkletNode>();

/**
 * Меняет предел на ходу и отвечает тем, что стоит сейчас.
 *
 * Из интерфейса не зовётся, только из консоли (`airel.attenuation`): предел
 * выбирается ухом — переключением туда-сюда на одном и том же голосе, — а
 * пересоздание модели стоит полсекунды на разбор весов, за которые сравнивать
 * становится нечего. Тридцать шесть поставлены так же и ещё не окончательны.
 * Когда окажутся окончательны — эту ручку можно убирать вместе с сообщением
 * `attenuation` в обработчике.
 */
export function setAttenuation(value?: number): number {
  if (typeof value === "number") {
    attenuation = value;
    ready?.node?.port.postMessage({ type: "attenuation", value });
    for (const node of previewNodes) node.port.postMessage({ type: "attenuation", value });
  }

  return attenuation;
}

/** Ограничения браузерной обработки — ровно то, что умеет менять LiveKit. */
type Constraints = Pick<
  AudioCaptureOptions,
  "autoGainControl" | "echoCancellation" | "noiseSuppression" | "voiceIsolation"
>;

export type DenoiserState =
  /** В этой сборке модели нет: `npm run denoiser:build` не запускали. */
  | "absent"
  /** Есть, но ещё не понадобилась. */
  | "idle"
  /** Грузится. Это те самые 450 мс на разбор весов плюс девять мегабайт в вебе. */
  | "loading"
  /** Работает. */
  | "ready"
  /** Не завелась. Звук идёт мимо, штатная обработка возвращена. */
  | "failed";

export interface DenoiserStatus {
  state: DenoiserState;
  /**
   * Добавленная задержка, миллисекунды.
   *
   * Складывается из кадра запаса на кадровку и заглядывания модели вперёд —
   * второе читается из весов, а не берётся из статьи. `null` — прочитать не
   * вышло, а врать тут нечем.
   */
  delayMs: number | null;
  /** Что пошло не так — для настроек. */
  problem: string | null;
}

/**
 * Состояние — стором zustand, а не своим списком подписчиков: см. `volume.ts`.
 *
 * Экран настроек зовёт его хуком, а сам тракт — `denoiserStatus()`: сообщения
 * от обработчика приходят вне отрисовки, и ждать её тут нечего.
 */
export const useDenoiser = create<DenoiserStatus>()(() => ({
  state: denoiserBuilt() ? "idle" : "absent",
  delayMs: null,
  problem: null,
}));

function announce(next: Partial<DenoiserStatus>): void {
  useDenoiser.setState(next);
}

export function denoiserStatus(): DenoiserStatus {
  return useDenoiser.getState();
}

export { denoiserBuilt } from "./load";

/**
 * Заведённый движок: он один на всё приложение.
 *
 * Один — потому что заводится он долго (девять мегабайт и 450 мс на разбор
 * весов), а микрофон включают и выключают часто. Пересоздавать всё это на
 * каждый вход в канал значило бы платить ту же цену снова и снова.
 */
interface Engine {
  context: AudioContext;
  /** Узел модели. Пусто — тракт собран без неё, только с усилением. */
  node: AudioWorkletNode | null;
  /** Длина кадра в отсчётах — так, как её назвала сама модель. */
  frame: number;
  /** Завёлся ли в этом потоке затвор. Не завёлся — тракт идёт без него. */
  gated: boolean;
  /** То же для выравнивателя. */
  leveled: boolean;
}

let engine: Promise<Engine> | null = null;

/**
 * Он же, когда уже готов.
 *
 * Дубль обещания нужен ради синхронности: отключать узлы через `then` нельзя
 * — отключение случится микрозадачей позже, то есть уже после того, как на
 * их место подключили следующие, и оборвёт как раз новые. Так и вышло при
 * переходе между каналами: второй микрофон уходил в тишину.
 */
let ready: Engine | null = null;

/** Что сейчас подключено. Отдельно от движка: живёт от микрофона до микрофона. */
interface Wiring {
  source: MediaStreamAudioSourceNode;
  gain: GainNode;
  /** Выравниватель. Пусто — не нужен (подавление выключено) или не завёлся. */
  leveler: AudioWorkletNode | null;
  /**
   * Последнее звено перед моделью: выравниватель, а без него — усиление.
   *
   * Записано явно по той же причине, что `before`: обойти модель и убрать
   * выравниватель можно в любом порядке, и каждый обход должен знать, что
   * сейчас стоит с другой стороны.
   */
  front: AudioNode;
  /** Куда идёт `front`: в модель, а если её нет или обошли — в тембр. */
  next: AudioNode;
  tone: VoiceTone;
  /** Затвор. Пусто — не завёлся, и звук идёт открытым. */
  gate: AudioWorkletNode | null;
  sink: MediaStreamAudioDestinationNode;
  /**
   * Кто подаёт на затвор — а если его нет, то прямо на приёмник.
   *
   * Это последний фильтр тембра. Записан явно, чтобы затвор можно было
   * убрать после отказа независимо от наличия модели перед фильтрами.
   */
  before: AudioNode;
}

let wiring: Wiring | null = null;

/** Усиление своего микрофона. Держится снаружи связки: её пересобирают чаще. */
let micGain = 1;

/** Ставить ли выравниватель. Решается при сборке тракта, как и модель. */
let levelWanted = true;

/**
 * Уровень своей речи, который выучил выравниватель, dBFS.
 *
 * Тракт пересобирается при каждом входе в канал и смене устройства, и новый
 * выравниватель начинает с этого числа, а не учится заново: иначе первые
 * слова тихого человека каждый раз уходили бы в модель тихими. Живёт до
 * перезагрузки страницы — микрофон за это время меняют редко, а ошибка
 * исправляется сама за пару секунд речи.
 */
let heardLevel: number | null = null;

/** Меняет усиление на ходу — ползунок должен слышаться сразу, а не с звонка. */
export function setMicGain(value: number): void {
  micGain = value;
  if (wiring) wiring.gain.gain.value = pushOpen() ? value : 0;
}

/*
 * Рация глушит микрофон тем же усилением: закрытая — ноль. Нарастание за
 * несколько миллисекунд, а не скачком: резкий ноль щёлкает в начале слова.
 */
onPushChange((open) => {
  if (!wiring) return;
  const now = wiring.gain.context.currentTime;
  wiring.gain.gain.cancelScheduledValues(now);
  wiring.gain.gain.setTargetAtTime(open ? micGain : 0, now, 0.005);
});

/** Три полосы одной ручки тембра. Все стоят после модели, если она включена. */
interface VoiceTone {
  context: AudioContext;
  highpass: BiquadFilterNode;
  bass: BiquadFilterNode;
  presence: BiquadFilterNode;
}

/** Мягкая поправка по умолчанию совпадает с настройкой в `media/audio.ts`. */
let voiceClarity = 0.5;

/**
 * Меняет тембр без пересборки дорожки.
 *
 * Параметры едут к новому значению за несколько миллисекунд: мгновенная
 * смена коэффициентов фильтра щёлкает как раз тогда, когда человек двигает
 * ползунок и внимательно слушает результат.
 */
export function setVoiceClarity(value: number): void {
  voiceClarity = clampClarity(value);
  if (wiring) tuneVoice(wiring.tone, voiceClarity, true);
}

function clampClarity(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0.5;
}

/** Собирает эквалайзер, который меняет только относительный баланс голоса. */
function voiceTone(context: AudioContext, clarity: number): VoiceTone {
  const highpass = context.createBiquadFilter();
  highpass.type = "highpass";
  highpass.Q.value = Math.SQRT1_2;

  const bass = context.createBiquadFilter();
  bass.type = "lowshelf";
  bass.frequency.value = 180;

  const presence = context.createBiquadFilter();
  presence.type = "peaking";
  presence.frequency.value = 3_000;
  presence.Q.value = 0.8;

  const tone = { context, highpass, bass, presence };
  tuneVoice(tone, clarity, false);
  return tone;
}

/** Переводит одну понятную шкалу в три параметра фильтров. */
function tuneVoice(tone: VoiceTone, value: number, smooth: boolean): void {
  const clarity = clampClarity(value);
  // В середине ровно обещанный мягкий профиль: 90 Гц, −3 дБ, +2 дБ.
  const highpass = clarity <= 0.5
    ? 20 + clarity * 140
    : 90 + (clarity - 0.5) * 60;

  moveParam(tone.highpass.frequency, highpass, tone.context, smooth);
  moveParam(tone.bass.gain, clarity === 0 ? 0 : -6 * clarity, tone.context, smooth);
  moveParam(tone.presence.gain, 4 * clarity, tone.context, smooth);
}

function moveParam(param: AudioParam, value: number, context: AudioContext, smooth: boolean): void {
  if (!smooth) {
    param.value = value;
    return;
  }

  param.cancelScheduledValues(context.currentTime);
  param.setTargetAtTime(value, context.currentTime, 0.02);
}

/** Порог затвора в долях полной шкалы. Ноль — «авто», см. `gate.js`. */
let gateAt = 0;

/** Меняет порог на ходу. Причина та же, что у громкости: ползунок — сейчас. */
export function setGateThreshold(value: number): void {
  gateAt = value;
  wiring?.gate?.port.postMessage({ type: "threshold", value });
}

/** Что затвор думает прямо сейчас. Пусто — тракта нет или затвор не завёлся. */
export interface GateReport {
  open: boolean;
  /** Уровень последнего кванта и оценка шумового пола, доли полной шкалы. */
  level: number;
  floor: number;
  /** Порог, по которому он и решает. */
  threshold: number;
  /** Задан рукой или посчитан сам. */
  manual: boolean;
  broken: boolean;
}

let awaitingReport: ((value: GateReport | null) => void) | null = null;

/**
 * Спрашивает затвор о его состоянии — для консоли.
 *
 * Через сообщение, а не чтением поля: считает он в звуковом потоке, и
 * отсюда до его чисел никак иначе не дотянуться. Со сроком, потому что
 * молчащий поток (усыплённый между звонками) не ответит никогда.
 */
export function gateReport(): Promise<GateReport | null> {
  const node = wiring?.gate;
  if (!node) return Promise.resolve(null);

  return new Promise((resolve) => {
    awaitingReport = resolve;
    node.port.postMessage({ type: "report" });

    setTimeout(() => {
      if (awaitingReport !== resolve) return;
      awaitingReport = null;
      resolve(null);
    }, 200);
  });
}

/** Что выравниватель думает прямо сейчас. Пусто — тракта нет или он не стоит. */
export interface LevelerReport {
  /** Выученный уровень речи и шумовой пол, dBFS. */
  speech: number;
  floor: number;
  /** Сколько он сейчас прибавляет, дБ. */
  gain: number;
  /** Слышал ли он уже достаточно речи, чтобы верить своему уровню. */
  learned: boolean;
  broken: boolean;
}

let awaitingLeveler: ((value: LevelerReport | null) => void) | null = null;

/** То же, что `gateReport`, для выравнивателя — тоже для консоли. */
export function levelerReport(): Promise<LevelerReport | null> {
  const node = wiring?.leveler;
  if (!node) return Promise.resolve(null);

  return new Promise((resolve) => {
    awaitingLeveler = resolve;
    node.port.postMessage({ type: "report" });

    setTimeout(() => {
      if (awaitingLeveler !== resolve) return;
      awaitingLeveler = null;
      resolve(null);
    }, 200);
  });
}

/**
 * Тракт без модели: усиление, выравниватель, тембр и затвор.
 *
 * Нужен потому, что громкость и тембр микрофона настраивают и те, кто
 * шумодав не включал. Тянуть ради них девять мегабайт незачем — граф
 * собирается тот же, просто без узла модели посередине.
 */
async function bare(): Promise<Engine> {
  const context = new AudioContext({ sampleRate: MODEL_RATE, latencyHint: "interactive" });
  await context.resume();
  ready = {
    context,
    node: null,
    frame: 0,
    gated: await loadModule(context, gateSource()),
    leveled: await loadModule(context, levelerSource()),
  };
  return ready;
}

/**
 * Заводит в потоке затвор или выравниватель.
 *
 * Отказ здесь ничего не роняет и наружу не выходит: не завелось звено —
 * тракт собирается без него. Без затвора микрофон открыт всегда, без
 * выравнивателя звучит как есть. Это ровно то, чем ошибаться можно, — в
 * отличие от запертого намертво.
 */
async function loadModule(context: AudioContext, source: string): Promise<boolean> {
  const blob = URL.createObjectURL(new Blob([source], { type: "text/javascript" }));

  try {
    await context.audioWorklet.addModule(blob);
    return true;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(blob);
  }
}

/** Узел затвора: свой на каждое подключение, состояние у него своё. */
function gateNode(context: AudioContext): AudioWorkletNode | null {
  try {
    const node = new AudioWorkletNode(context, "gate", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      channelCountMode: "explicit",
      processorOptions: { threshold: gateAt },
    });

    node.port.onmessage = (event: MessageEvent) => {
      const message = event.data as { type?: string; reason?: string } & Partial<GateReport>;

      if (message.type === "report" && awaitingReport) {
        awaitingReport(message as GateReport);
        awaitingReport = null;
        return;
      }

      if (message.type === "failed") {
        announce({ problem: `затвор сдался (${message.reason ?? "неизвестно"}) — микрофон открыт всегда` });
      }
    };

    // Упавший обработчик отдаёт тишину, а тишина здесь — немой микрофон.
    node.onprocessorerror = () => {
      announce({ problem: "затвор упал — микрофон открыт всегда" });
      ungate(wiring);
    };

    return node;
  } catch {
    return null;
  }
}

/**
 * Узел выравнивателя: свой на каждое подключение, но с уровнем, выученным
 * прошлым (`heardLevel`).
 *
 * `onFailed` — что делать, если он упал: в звонке и в проверке обходят его
 * по-разному.
 */
function levelerNode(context: AudioContext, onFailed: () => void): AudioWorkletNode | null {
  try {
    const node = new AudioWorkletNode(context, "leveler", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      channelCountMode: "explicit",
      processorOptions: { start: heardLevel },
    });

    node.port.onmessage = (event: MessageEvent) => {
      const message = event.data as { type?: string; speech?: number } & Partial<LevelerReport>;

      if (message.type === "level" && typeof message.speech === "number") {
        heardLevel = message.speech;
        return;
      }

      if (message.type === "report" && awaitingLeveler) {
        awaitingLeveler(message as LevelerReport);
        awaitingLeveler = null;
      }
    };

    // Упавший отдаёт тишину. Сдавшийся (`failed`) — звук как есть, и это
    // ничего не ломает: тихий так и останется тихим.
    node.onprocessorerror = onFailed;

    return node;
  } catch {
    return null;
  }
}

function warm(model: boolean): Promise<Engine> {
  /*
   * Узел модели посреди уже собранного графа не появится, а лишний сам не
   * исчезнет. Значит тракт пересобирается всякий раз, когда просят не то,
   * что собрано.
   *
   * В обе стороны, а не только под модель. Обратный случай выглядел
   * безобидно — «тракт с моделью годится и тем, кому нужно одно усиление», —
   * но означал он вот что: выключенное сильное подавление продолжало работать
   * до перезагрузки страницы, и вдобавок вторым слоем поверх вернувшегося
   * платформенного. Ровно то сочетание, от которого съедаются согласные.
   */
  if (ready && model !== !!ready.node) {
    void ready.context.close();
    engine = null;
    ready = null;
  }

  if (!engine) engine = model ? build() : bare();
  return engine;
}

async function build(): Promise<Engine> {
  announce({ state: "loading", problem: null });

  /*
   * Свой поток обработки, а не тот, что даёт LiveKit.
   *
   * Во-первых, частота: модель обучена на 48 кГц, а поток по умолчанию идёт
   * на той, что у железа, — и на 44.1 кадр в 480 отсчётов перестаёт быть
   * десятью миллисекундами. Здесь она задана прямо, а пересчёт делает сам
   * браузер на входе.
   *
   * Во-вторых, разбор весов держит звуковой поток эти самые 450 мс. В общем
   * потоке это была бы пауза во всём звуке; в своём — пауза в пустом графе,
   * к которому ещё ничего не подключено.
   */
  const context = new AudioContext({ sampleRate: MODEL_RATE, latencyHint: "interactive" });

  try {
    if (context.sampleRate !== MODEL_RATE) {
      throw new Error(`браузер не дал ${MODEL_RATE} Гц, поток идёт на ${context.sampleRate}`);
    }

    /*
     * Поток надо разбудить до того, как с обработчиком заговорят.
     *
     * Сообщения в `port` разбираются в звуковом потоке, а спящий поток не
     * разбирает ничего: ответа `ready` не пришло бы никогда, и микрофон
     * встал бы ждать его навсегда. Разрешение на это уже есть — сюда
     * приходят из включения микрофона, то есть по действию человека.
     */
    await context.resume();

    const blob = URL.createObjectURL(
      new Blob([await workletSource()], { type: "text/javascript" }),
    );

    try {
      await context.audioWorklet.addModule(blob);
    } finally {
      URL.revokeObjectURL(blob);
    }

    const [wasm, model] = await Promise.all([wasmBytes(), modelBytes()]);
    // До пересылки: она забирает буфер себе, и читать его будет уже нечем.
    const settings = await modelSettings(model);

    const node = new AudioWorkletNode(context, "denoiser", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      // Модель одноканальная. Стерео с микрофона сводится браузером здесь же,
      // на входе узла, — иначе кадр приехал бы вдвое длиннее ожидаемого.
      channelCount: 1,
      channelCountMode: "explicit",
    });

    const frame = await limited(start(node, wasm, model), 15_000, "модель не завелась вовремя");

    /*
     * Веса и поток должны говорить об одном и том же.
     *
     * Расхождение тут не поломка, а нечто худшее: всё работает, звук идёт, и
     * только звучит не так — кадр в 480 отсчётов на другой частоте перестаёт
     * быть десятью миллисекундами, и модель слышит свой вход растянутым.
     */
    if (settings && (settings.rate !== MODEL_RATE || settings.hop !== frame)) {
      throw new Error(
        `веса не про этот поток: ${settings.rate} Гц, кадр ${settings.hop} против ${frame}`,
      );
    }

    /*
     * Задержка: кадр запаса на кадровку плюс заглядывание модели вперёд.
     *
     * Кадр запаса — ровно кадр, не «до»: вход и выход ходят одним квантом, и
     * сколько недобрало одно кольцо, столько лежит в другом (проверено в
     * `worklet.test.js`). Заглядывание берётся из самих весов: наружу оно не
     * выведено, а число из статьи и число в этом файле совпадать не обязаны.
     */
    announce({
      state: "ready",
      delayMs: settings ? ((1 + settings.lookahead) * frame * 1000) / MODEL_RATE : null,
      problem: null,
    });

    /*
     * Упавший обработчик выключается браузером насовсем и с этого мига
     * отдаёт тишину. Это ровно тот случай, ради которого всё здесь и
     * писалось наоборот: звук пускается мимо него напрямую, а в настройках
     * появляется отказ.
     */
    node.onprocessorerror = () => {
      announce({ state: "failed", delayMs: null, problem: "обработчик упал" });
      bypass();
    };

    ready = {
      context,
      node,
      frame,
      gated: await loadModule(context, gateSource()),
      leveled: await loadModule(context, levelerSource()),
    };
    return ready;
  } catch (error) {
    void context.close();
    engine = null;
    ready = null;
    announce({ state: "failed", delayMs: null, problem: reasonOf(error) });
    throw error;
  }
}

/** Отдаёт обработчику всё, что нужно, и ждёт ответа. Возвращает длину кадра. */
function start(node: AudioWorkletNode, wasm: ArrayBuffer, model: ArrayBuffer) {
  return new Promise<number>((resolve, reject) => {
    node.port.onmessage = (event: MessageEvent) => {
      const message = event.data as { type: string; frame?: number; reason?: string };

      switch (message.type) {
        case "ready":
          resolve(message.frame ?? 0);
          break;
        case "failed":
          // И после `ready` тоже: обработчик мог сдаться на живом звуке.
          // Тогда он пропускает его как есть, и сказать об этом важнее, чем
          // то, что заведение когда-то удалось.
          announce({ state: "failed", delayMs: null, problem: message.reason ?? "обработчик сдался" });
          reject(new Error(message.reason ?? "обработчик не завёлся"));
          break;
        case "starved":
          // Выход не успел набрать квант. Один раз — рябь при заведении;
          // если машина не тянет вовсе, это первое, что о ней скажут.
          announce({ problem: "звук проваливается — машина не успевает" });
          break;
      }
    };

    node.onprocessorerror = () => reject(new Error("обработчик упал при заведении"));

    /*
     * И движок, и веса едут переносом: копировать двадцать мегабайт незачем,
     * здесь они больше не нужны. Движок именно байтами — почему, написано в
     * `wasmBytes`.
     */
    node.port.postMessage({ type: "start", wasm, model, attenuation }, [wasm, model]);
  });
}

/**
 * Обработчик дорожки в том виде, в каком его ждёт `livekit-client`.
 *
 * Создаётся на комнату, а движок под ним общий: `init` вызывается всякий
 * раз, когда берётся микрофон, — и при первом включении, и при смене
 * устройства, и при выходе из заглушения.
 */
class MicChain implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
  name = "airel-mic";

  /**
   * Дорожка, которую LiveKit поставит вместо микрофонной.
   *
   * Пустая — значит обошлось без нас: LiveKit оставит исходную. Класть сюда
   * саму микрофонную дорожку нельзя ни в каком случае — при разборе
   * обработчика он её остановит, и микрофон умрёт совсем.
   */
  processedTrack?: MediaStreamTrack;

  /** Своя связка — чтобы разбирать именно её, а не чью-то чужую. */
  private wiring: Wiring | null = null;

  constructor(
    private readonly fallback: Constraints,
    private readonly model: boolean,
  ) {}

  async init(options: AudioProcessorOptions): Promise<void> {
    try {
      /*
       * Со сроком: `init` держит включение микрофона, и зависшая загрузка
       * означала бы микрофон, который не включается вовсе. Девять
       * мегабайт по медленной связи — это долго, но не полминуты.
       */
      const warmed = await limited(warm(this.model), 30_000, "шумодав не загрузился вовремя");
      this.wiring = attach(warmed, options.track);
      this.processedTrack = this.wiring.sink.stream.getAudioTracks()[0];
    } catch {
      this.processedTrack = undefined;
      await retreat(options, this.fallback);
    }
  }

  /** Дорожку сменили: устройство, выход из заглушения, восстановление связи. */
  async restart(options: AudioProcessorOptions): Promise<void> {
    await this.init(options);
  }

  async destroy(): Promise<void> {
    this.processedTrack = undefined;
    detach(this.wiring);
    this.wiring = null;
  }
}

/**
 * Тракт для дорожки микрофона — одной точкой на комнату.
 *
 * Собирается всегда, а не только под моделью: тембр и затвор нужны на любой
 * ступени подавления. Раньше тракт при единичном усилении и без модели не
 * собирался вовсе — тогда в нём и правда ничего не происходило.
 */
export function createMicChain(
  fallback: Constraints,
  options: { model: boolean; gain: number; clarity: number; threshold: number; level: boolean },
): TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
  micGain = options.gain;
  voiceClarity = clampClarity(options.clarity);
  gateAt = options.threshold;
  levelWanted = options.level;
  return new MicChain(fallback, options.model && denoiserBuilt());
}

/** Подключает микрофон ко входу тракта. */
function attach(warmed: Engine, track: MediaStreamTrack): Wiring {
  // Прежнее убирается, но поток не усыпляется: он тут же понадобится снова.
  unwire(wiring);

  const source = warmed.context.createMediaStreamSource(new MediaStream([track]));
  /*
   * Приёмник новый на каждое подключение, а не общий.
   *
   * LiveKit при разборе обработчика останавливает отданную дорожку, а
   * остановленная не воскресает — общий приёмник пережил бы ровно одно
   * включение микрофона.
   */
  const sink = warmed.context.createMediaStreamDestination();

  /*
   * Усиление стоит до модели, а не после.
   *
   * Модель обучена на речи обычного уровня: подсунуть ей тихий сигнал, а
   * потом вытянуть результат — значит вытянуть вместе с ним и то, что она
   * приняла за фон и оставила. Сначала приводим уровень, потом чистим.
   */
  const gain = warmed.context.createGain();
  gain.gain.value = pushOpen() ? micGain : 0;

  /*
   * Выравниватель — сразу за ручным усилением и до модели, по той же
   * причине: ей нужен голос обычного уровня, а тихий она стирает. За ручным,
   * а не перед ним — он только поднимает, и прибавленное рукой ему остаётся
   * не прибавлять.
   */
  const leveler =
    levelWanted && warmed.leveled ? levelerNode(warmed.context, () => unlevel(wiring)) : null;

  /*
   * Тембр стоит после шумодава: модель получает естественный спектр, на
   * котором обучена, а человек слышит одну и ту же коррекцию на любой
   * ступени подавления. Ноль на ручке делает полосы практически прозрачными.
   */
  const tone = voiceTone(warmed.context, voiceClarity);

  /*
   * Затвор — последним звеном, после модели.
   *
   * После неё, потому что мерить он должен то, что уйдёт в канал: до модели
   * в сигнале ещё есть весь фон комнаты, и порог пришлось бы поднимать до
   * уровня, на котором отсекается и тихая речь. После — фона нет, и разница
   * между речью и щелчком по клавише становится теми самыми тридцатью
   * децибелами, по которым он и решает.
   */
  const gate = warmed.gated ? gateNode(warmed.context) : null;

  source.connect(gain);
  const front: AudioNode = leveler ? gain.connect(leveler) : gain;
  const next: AudioNode = warmed.node ?? tone.highpass;
  if (warmed.node) front.connect(warmed.node).connect(tone.highpass);
  else front.connect(tone.highpass);
  tone.highpass.connect(tone.bass).connect(tone.presence);
  const before: AudioNode = tone.presence;
  if (gate) before.connect(gate).connect(sink);
  else before.connect(sink);

  wiring = { source, gain, leveler, front, next, tone, gate, sink, before };

  void warmed.context.resume();

  return wiring;
}

/**
 * Разбирает связку — и только её.
 *
 * Разбирается не «что подключено», а именно та связка, которую просят
 * убрать. Разница видна при переходе между каналами: старая комната
 * разбирается сама по себе и может дойти до разбора уже после того, как
 * новая взяла микрофон, — и без этой проверки заглушила бы её.
 */
function unwire(target: Wiring | null): void {
  if (!target || wiring !== target) return;

  target.source.disconnect();
  target.gain.disconnect();
  target.leveler?.disconnect();
  /*
   * Узел модели общий и связку переживает — его надо отсоединять отдельно.
   * Целиком, а не от названного места: выход у него один, и куда именно он
   * подключён, зависит и от затвора, и от того, не обошли ли его самого.
   * Отсоединение «от всего» на это не смотрит и ничем не бросается.
   */
  ready?.node?.disconnect();
  target.tone.highpass.disconnect();
  target.tone.bass.disconnect();
  target.tone.presence.disconnect();
  target.gate?.disconnect();
  wiring = null;
}

/** Пускает звук мимо обработчика, не трогая дорожку, которую уже отдали. */
function bypass(): void {
  if (!wiring) return;

  ready?.node?.disconnect();
  wiring.front.disconnect();
  // Мимо модели, но не мимо усиления, выравнивателя, тембра и затвора: их
  // ползунки к её падению отношения не имеют и работать не перестают.
  wiring.front.connect(wiring.tone.highpass);
  wiring.next = wiring.tone.highpass;
}

/**
 * То же для выравнивателя: звук идёт мимо него, как есть.
 *
 * Усиление ведётся туда, куда вёл он сам, — в модель или, если её обошли, в
 * тембр. Это и записано в `next`.
 */
function unlevel(target: Wiring | null): void {
  if (!target?.leveler) return;

  announce({ problem: "выравниватель упал — микрофон звучит как есть" });
  target.leveler.disconnect();
  target.gain.disconnect();
  target.gain.connect(target.next);
  target.leveler = null;
  target.front = target.gain;
}

/**
 * То же для затвора: звук идёт мимо него, открытым.
 *
 * Отдельно от `bypass`, потому что случай обратный — упасть могло любое из
 * двух звеньев, и уцелевшее должно работать дальше. Отсюда и `before`: если
 * модель уже обошли, к приёмнику надо вести усиление, а не её.
 */
function ungate(target: Wiring | null): void {
  if (!target?.gate) return;

  target.gate.disconnect();
  target.before.connect(target.sink);
  target.gate = null;
}

/** То же, но насовсем: микрофон отпустили, движок можно и усыпить. */
function detach(target: Wiring | null): void {
  const playing = !!target && wiring === target;
  unwire(target);

  // Спящий поток не считает ничего: держать пять процентов ядра между
  // звонками не за что.
  if (playing) void ready?.context.suspend();
}

/**
 * Проверочный тракт: те же звенья, что в звонке, — усиление, выравниватель,
 * модель, тембр, затвор, — но целиком свои: свой звуковой поток, свои узлы,
 * свой разбор весов.
 *
 * Свои, а не общие, потому что настройки открывают и посреди звонка. Движок
 * держит одну связку на всё приложение, и подключение проверки к нему увело
 * бы микрофон звонка в тишину — `attach` снимает прежнюю связку намеренно.
 * Цена самостоятельности — вторые веса в памяти, пока открыт экран настроек;
 * `close` возвращает всё.
 *
 * В общее состояние (`useDenoiser`) тракт не пишет ни слова: оно рассказывает
 * про звонок, и отказ проверки посреди живого разговора выглядел бы как отказ
 * рабочего шумодава.
 *
 * Правило внутри то же: микрофон важнее шумодава. Не заведшееся звено
 * выпадает из цепочки, а не роняет проверку, — заведётся ли оно в звонке,
 * вопрос звонка, и отвечает на него `MicChain` со своим `retreat`.
 */
export interface PreviewTract {
  /** Выход тракта — то, что ушло бы в канал. Его и записывает «Проверить». */
  stream: MediaStream;
  /**
   * Ответвление на полоску уровня: тот же сигнал, что видит затвор, — после
   * модели и до него. По нему ручной порог выставляется по правде.
   */
  analyser: AnalyserNode;
  /** Собралась ли модель: просили, есть в сборке и завелась. */
  modelOn: boolean;
  setGain(value: number): void;
  setClarity(value: number): void;
  setThreshold(value: number): void;
  /** Разбудить поток по действию человека — перед записью. */
  wake(): void;
  close(): void;
}

export async function createPreviewTract(
  track: MediaStreamTrack,
  asked: {
    model: boolean;
    gain: number;
    clarity: number;
    threshold: number;
    /** Ставить ли выравниватель — как в звонке, по ступени подавления. */
    level: boolean;
    signal?: AbortSignal;
    fallback?: MediaTrackConstraints;
    onStatus?: (status: DenoiserStatus) => void;
  },
): Promise<PreviewTract> {
  const context = new AudioContext({ sampleRate: MODEL_RATE, latencyHint: "interactive" });
  let node: AudioWorkletNode | null = null;
  let leveler: AudioWorkletNode | null = null;
  let gate: AudioWorkletNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let sink: MediaStreamAudioDestinationNode | null = null;
  let gain: GainNode | null = null;
  let tone: VoiceTone | null = null;
  let analyser: AnalyserNode | null = null;
  let closed = false;
  let failed = false;
  let fallingBack: Promise<void> | null = null;

  const report = (status: DenoiserStatus) => {
    if (!closed) asked.onStatus?.(status);
  };

  const close = () => {
    if (closed) return;
    closed = true;
    asked.signal?.removeEventListener("abort", close);
    if (node) {
      previewNodes.delete(node);
      node.port.close();
      node.disconnect();
    }
    gate?.port.close();
    gate?.disconnect();
    leveler?.port.close();
    leveler?.disconnect();
    source?.disconnect();
    gain?.disconnect();
    tone?.highpass.disconnect();
    tone?.bass.disconnect();
    tone?.presence.disconnect();
    analyser?.disconnect();
    for (const output of sink?.stream.getTracks() ?? []) output.stop();
    void context.close().catch(() => {});
  };

  const ensureOpen = () => {
    if (closed || asked.signal?.aborted) throw new DOMException("Проверка закрыта", "AbortError");
  };

  asked.signal?.addEventListener("abort", close, { once: true });

  // Отказ после запуска тоже возвращает штатную обработку. Статус принадлежит
  // проверке: обновлять состояние модели идущего звонка здесь нельзя.
  const fallBack = async (error: unknown) => {
    if (closed || failed) return;
    failed = true;
    if (node) {
      previewNodes.delete(node);
      node.port.close();
      node.disconnect();
      node = null;
      if (gain && tone) {
        const front = leveler ?? gain;
        front.disconnect();
        front.connect(tone.highpass);
      }
    }

    const problem = reasonOf(error);
    report({ state: "failed", delayMs: null, problem });
    if (!asked.fallback) return;

    try {
      await track.applyConstraints(asked.fallback);
      report({ state: "failed", delayMs: null, problem: problem + " — используется обычное подавление" });
    } catch {
      report({ state: "failed", delayMs: null, problem: problem + " — обычное подавление тоже не включилось" });
    }
  };

  try {
    ensureOpen();
    await context.resume();
    ensureOpen();

    if (asked.model && denoiserBuilt()) {
      report({ state: "loading", delayMs: null, problem: null });
      try {
        const model = await previewModel(context, ensureOpen);
        node = model.node;
        ensureOpen();
        previewNodes.add(node);
        node.port.onmessage = (event: MessageEvent) => {
          const message = event.data as { type?: string; reason?: string };
          if (message.type === "failed") fallingBack = fallBack(message.reason ?? "обработчик сдался");
          if (message.type === "starved") {
            report({ state: "ready", delayMs: model.delayMs, problem: "звук проваливается — машина не успевает" });
          }
        };
        node.onprocessorerror = () => { fallingBack = fallBack("обработчик упал"); };
        report({ state: "ready", delayMs: model.delayMs, problem: null });
      } catch (error) {
        ensureOpen();
        await fallBack(error);
        ensureOpen();
      }
    } else {
      report({ state: denoiserBuilt() ? "idle" : "absent", delayMs: null, problem: null });
    }

    const leveled = asked.level && (await loadModule(context, levelerSource()));
    ensureOpen();

    source = context.createMediaStreamSource(new MediaStream([track]));
    sink = context.createMediaStreamDestination();
    analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    gain = context.createGain();
    gain.gain.value = asked.gain;
    tone = voiceTone(context, asked.clarity);

    // Упавший выравниватель обходится тем же путём, что в звонке: усиление
    // ведётся туда, куда вёл он, — в модель, а без неё в тембр.
    const unleveled = () => {
      if (closed || !leveler) return;
      leveler.port.close();
      leveler.disconnect();
      leveler = null;
      gain!.disconnect();
      gain!.connect(node ?? tone!.highpass);
    };
    leveler = leveled ? levelerNode(context, unleveled) : null;

    source.connect(gain);
    const front: AudioNode = leveler ? gain.connect(leveler) : gain;
    const processed: AudioNode = node ? front.connect(node) : front;
    processed.connect(tone.highpass).connect(tone.bass).connect(tone.presence).connect(analyser);

    const ungated = () => {
      if (closed || !gate) return;
      gate.port.close();
      gate.disconnect();
      gate = null;
      analyser!.connect(sink!);
    };

    const gated = await loadModule(context, gateSource());
    ensureOpen();
    if (gated) {
      try {
        gate = new AudioWorkletNode(context, "gate", {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1],
          channelCount: 1,
          channelCountMode: "explicit",
          processorOptions: { threshold: asked.threshold },
        });
        gate.port.onmessage = (event: MessageEvent) => {
          if ((event.data as { type?: string }).type === "failed") ungated();
        };
        gate.onprocessorerror = ungated;
      } catch {
        gate = null;
      }
    }

    if (gate) analyser.connect(gate).connect(sink);
    else analyser.connect(sink);
    await fallingBack;
    ensureOpen();

    return {
      stream: sink.stream,
      analyser,
      get modelOn() { return !!node && !closed; },
      setGain(value: number) { if (!closed) gain!.gain.value = value; },
      setClarity(value: number) { if (!closed) tuneVoice(tone!, value, true); },
      setThreshold(value: number) { if (!closed) gate?.port.postMessage({ type: "threshold", value }); },
      wake() { if (!closed) void context.resume().catch(() => {}); },
      close,
    };
  } catch (error) {
    close();
    throw error;
  }
}

/** Своя модель проверки: те же проверки весов, без записи в состояние звонка. */
async function previewModel(
  context: AudioContext,
  ensureOpen: () => void,
): Promise<{ node: AudioWorkletNode; delayMs: number | null }> {
  if (context.sampleRate !== MODEL_RATE) {
    throw new Error("браузер не дал 48000 Гц, поток идёт на " + context.sampleRate);
  }

  const body = await workletSource();
  ensureOpen();
  const blob = URL.createObjectURL(new Blob([body], { type: "text/javascript" }));
  try {
    await context.audioWorklet.addModule(blob);
  } finally {
    URL.revokeObjectURL(blob);
  }
  ensureOpen();

  const [wasm, model] = await Promise.all([wasmBytes(), modelBytes()]);
  ensureOpen();
  const settings = await modelSettings(model);
  ensureOpen();

  const node = new AudioWorkletNode(context, "denoiser", {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [1],
    channelCount: 1,
    channelCountMode: "explicit",
  });

  try {
    const frame = await limited(
      new Promise<number>((resolve, reject) => {
        node.port.onmessage = (event: MessageEvent) => {
          const message = event.data as { type: string; reason?: string; frame?: number };
          if (message.type === "ready") resolve(message.frame ?? 0);
          if (message.type === "failed") reject(new Error(message.reason ?? "обработчик не завёлся"));
        };
        node.onprocessorerror = () => reject(new Error("обработчик упал при заведении"));
        node.port.postMessage({ type: "start", wasm, model, attenuation }, [wasm, model]);
      }),
      15_000,
      "модель не завелась вовремя",
    );
    ensureOpen();
    if (settings && (settings.rate !== MODEL_RATE || settings.hop !== frame)) {
      throw new Error("веса не про этот поток: " + settings.rate + " Гц, кадр " + settings.hop + " против " + frame);
    }
    return { node, delayMs: settings ? ((1 + settings.lookahead) * frame * 1000) / MODEL_RATE : null };
  } catch (error) {
    node.port.close();
    node.disconnect();
    throw error;
  }
}

/** Работа со сроком: без него зависшая загрузка означала бы немой микрофон. */
function limited<T>(work: Promise<T>, ms: number, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    work,
    new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error(what)), ms); }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * Возврат к штатной обработке.
 *
 * Не украшение: с выключенным `noiseSuppression` и не заведшейся моделью
 * человек остался бы вовсе без подавления — то есть хуже, чем до всей этой
 * работы. Ограничения задаются полным набором: `applyConstraints` заменяет
 * набор целиком, и перечислить половину значило бы отпустить вторую.
 */
async function retreat(options: AudioProcessorOptions, fallback: Constraints): Promise<void> {
  const track = options.localTrack;
  if (!(track instanceof LocalAudioTrack)) return;

  try {
    await track.applyConstraints(fallback);
  } catch {
    // Устройство может и не уметь того, что просят. Сказать об этом уже
    // сказано — состояние `failed` видно в настройках.
  }
}

function reasonOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === "string" ? error : "неизвестно";
}
